import React, {useState} from 'react';
import {Stack, styled} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {
  Body,
  Button,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import formatCentsToDollars from '@/utils/formatCentsToDollars';
import translate from '@/utils/translate';
import {getCampaignNetworkPrettyName} from '@/config/deliverableKinds';
import {ConversationMessage} from '@/types/conversationMessage';
import {
  NEGOTIATION_SNAPSHOT_STATE,
  type NegotiationSnapshot as NegotiationSnapshotType,
  type NegotiationSnapshotState,
} from '@/types/negotiationSnapshot';
import GigDeliverableChangeRow from '@/components/gig-deliverable-change-row';
import WithdrawNegotiationOfferModal from '@/components/withdraw-negotiation-offer-modal';

const STATE_LABEL_KEY: Record<NegotiationSnapshotState, string> = {
  [NEGOTIATION_SNAPSHOT_STATE.pending]: 'Offer Sent',
  [NEGOTIATION_SNAPSHOT_STATE.accepted]: 'Offer Accepted',
  [NEGOTIATION_SNAPSHOT_STATE.withdrawn]: 'Offer Withdrawn',
  [NEGOTIATION_SNAPSHOT_STATE.rejected]: 'Offer Rejected',
} as const;

const STATE_COLOR_TOKEN: Record<NegotiationSnapshotState, string> = {
  [NEGOTIATION_SNAPSHOT_STATE.pending]: 'semantic.fg.warning',
  [NEGOTIATION_SNAPSHOT_STATE.accepted]: 'semantic.fg.success',
  [NEGOTIATION_SNAPSHOT_STATE.withdrawn]: 'semantic.fg.danger',
  [NEGOTIATION_SNAPSHOT_STATE.rejected]: 'semantic.fg.danger',
} as const;

const SnapshotCard = styled(Stack)({
  border: `1px solid`,
  borderRadius: '6px',
  padding: '15px',
  marginTop: '8px',
});

const SectionHeader = styled(Body)({
  marginTop: '10px',
  textAlign: 'left',
});

const ValuesRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: '6px',
  flexWrap: 'wrap',
});

const BadgeContainer = styled('span', {
  shouldForwardProp: (prop: string) => !['bgColor', 'textColor'].includes(prop),
})<{bgColor: string; textColor: string}>(({bgColor, textColor}) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '4px',
  fontWeight: 500,
  color: textColor,
  backgroundColor: bgColor,
}));

const FooterRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  paddingTop: '10px',
});

type NegotiationSnapshotProps = {
  snapshot: NegotiationSnapshotType;
  onWithdrawSuccess?: (message: ConversationMessage) => void;
};

function NegotiationSnapshotDisplay({
  snapshot,
  onWithdrawSuccess,
}: NegotiationSnapshotProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const displayState = snapshot.state;
  const colorToken = STATE_COLOR_TOKEN[displayState] ?? 'semantic.fg.primary';
  const stateLabel = translate(STATE_LABEL_KEY[displayState] ?? displayState);
  const costChanged = snapshot.costInCents !== snapshot.previousCostInCents;
  const timePeriodChanged =
    snapshot.contentAgreementTimePeriodInMonths != null &&
    snapshot.contentAgreementTimePeriodInMonths !==
      snapshot.previousContentAgreementTimePeriodInMonths;

  const brandNegotiationId = snapshot.brandNegotiation?.id;
  const paidGigId = snapshot.brandNegotiation?.contract?.id ?? null;
  const networkPrettyName = getCampaignNetworkPrettyName(
    snapshot.gigDeliverableChanges[0]?.campaignNetwork
  );

  return (
    <SnapshotCard
      sx={{borderColor: designSystemToken('semantic.bg.neutral-secondary')}}
    >
      <Label size="sm" color={designSystemToken('semantic.fg.primary')}>
        {translate('Proposed a Change To Terms')}
      </Label>

      <SectionHeader
        size="sm"
        color={designSystemToken('semantic.fg.secondary')}
      >
        {translate('Payment')}
      </SectionHeader>
      <ValuesRow>
        {costChanged ? (
          <Body
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
            sx={{textDecoration: 'line-through'}}
          >
            {formatCentsToDollars(snapshot.previousCostInCents)}
          </Body>
        ) : null}
        <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
          {formatCentsToDollars(snapshot.costInCents)}
        </Body>
      </ValuesRow>

      {snapshot.contentAgreementTimePeriodInMonths != null ? (
        <>
          <SectionHeader
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
          >
            {translate('Usage Rights Period')}
          </SectionHeader>
          <ValuesRow>
            {timePeriodChanged &&
            snapshot.previousContentAgreementTimePeriodInMonths != null ? (
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                sx={{textDecoration: 'line-through'}}
              >
                {translate('month', {
                  count: snapshot.previousContentAgreementTimePeriodInMonths,
                })}
              </Body>
            ) : null}
            <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
              {translate('month', {
                count: snapshot.contentAgreementTimePeriodInMonths,
              })}
            </Body>
          </ValuesRow>
        </>
      ) : null}

      {snapshot.gigDeliverableChanges.length > 0 ? (
        <>
          <SectionHeader
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
          >
            {translate('Deliverables')}
          </SectionHeader>
          <ValuesRow>
            {snapshot.gigDeliverableChanges.map((change) => (
              <GigDeliverableChangeRow
                key={change.kind}
                change={change}
                networkPrettyName={networkPrettyName}
              />
            ))}
          </ValuesRow>
        </>
      ) : null}

      <FooterRow>
        <BadgeContainer
          textColor={designSystemToken(colorToken)}
          bgColor={alpha(designSystemToken(colorToken), 0.1)}
        >
          <Body size="xs" color={designSystemToken(colorToken)}>
            {stateLabel}
          </Body>
        </BadgeContainer>
        {snapshot.brandNegotiation?.state ===
        NEGOTIATION_SNAPSHOT_STATE.pending ? (
          <Button
            mode="tinted"
            appearance="danger"
            size="small"
            onClick={() => setIsConfirmOpen(true)}
          >
            {translate('Withdraw Offer')}
          </Button>
        ) : null}
      </FooterRow>

      {brandNegotiationId ? (
        <WithdrawNegotiationOfferModal
          open={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          brandNegotiationId={brandNegotiationId}
          paidGigId={paidGigId}
          networkPrettyName={networkPrettyName}
          snapshot={snapshot}
          onWithdrawSuccess={onWithdrawSuccess}
        />
      ) : null}
    </SnapshotCard>
  );
}

export default NegotiationSnapshotDisplay;
